import { FC, useEffect, useState } from "react";
import { isMobile } from 'react-device-detect';
import Link from "next/link";

interface PaginationProps {
  totalPages: number;
  currentPageNo: number;
  onChange: (currentPageNo: number) => void;
  buildPageUrl?: (page: number) => string;
}

export const Pagination: FC<PaginationProps> = ({
  totalPages,
  currentPageNo,
  onChange,
  buildPageUrl,
}: PaginationProps) => {
  const pageNumbers: (string | number)[] = [];
  const totalVisiblePages = isMobile ? 5 : 7;
  const segmentCount = 2;
  const [items, setItems] = useState<any>([]);

  function updateCurrentPage(e: React.MouseEvent, currentPageNo: number) {
    if (buildPageUrl) {
      e.preventDefault();
    }
    onChange(currentPageNo);
  }

  useEffect(() => {
    if (totalPages <= totalVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      pageNumbers.push(1);
      if (currentPageNo < totalVisiblePages - segmentCount) {
        for (let i = 2; i <= (totalVisiblePages - segmentCount); i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push("...");
      } else if (currentPageNo > (totalPages + 1) - (totalVisiblePages - segmentCount)) {
        pageNumbers.push("...");
        for (let i = (totalPages + 1) - (totalVisiblePages - segmentCount); i < totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        pageNumbers.push("...");
        pageNumbers.push(currentPageNo - 1);
        pageNumbers.push(currentPageNo);
        pageNumbers.push(+currentPageNo + 1);
        pageNumbers.push("...");
      }

      pageNumbers.push(totalPages);
    }
    setItems(() => pageNumbers);
  }, [totalPages, currentPageNo]);

  useEffect(() => {
    onChange(currentPageNo);
  }, [currentPageNo]);

  if (totalPages <= 1) return <></>;

  const getPageHref = (page: number): string => {
    if (buildPageUrl) return buildPageUrl(page);
    return "#";
  };

  return (
    <nav aria-label="Pagination navigation" className="py-[40px] bg-gray-50">
      <ul className="pagination flex justify-center items-center gap-[8px] w-fit mx-auto cursor-pointer md:px-0">
        {currentPageNo !== 1 && <li className="page-item">
          <Link
            data-testid="prev"
            href={getPageHref(+currentPageNo - 1 > 0 ? +currentPageNo - 1 : 1)}
            aria-label="Previous page"
            className="page-link flex items-center justify-center px-[14px] py-[8px] rounded-[4px] w-[36px] h-[36px] hover:bg-primary-50"
            onClick={(e) => {
              currentPageNo > 1
                ? updateCurrentPage(e, +currentPageNo - 1)
                : updateCurrentPage(e, +currentPageNo);
            }}
          >
            <svg
              width="7"
              height="12"
              viewBox="0 0 7 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6 11L1 6L6 1"
                stroke="#333F48"
                strokeWidth="1.67"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Link>
        </li>}
        {items?.map((item: any, index: any) => (
          <li
            className={`page-item rounded-[4px] ${currentPageNo == item ? "bg-blue-400" : ""
              } `}
            key={index}
          >
            {item === "..." ? (
              <span
                className="page-link flex items-center justify-center small font-normal text-grey300 px-[14px] py-[8px] rounded-[4px] w-[36px] h-[36px]"
                aria-hidden="true"
              >
                ...
              </span>
            ) : currentPageNo == item ? (
              <span
                aria-current="page"
                aria-label={`Page ${item}, current page`}
                className="page-link flex items-center justify-center small font-normal text-white px-[14px] py-[8px] rounded-[4px] w-[36px] h-[36px]"
              >
                {item}
              </span>
            ) : (
              <Link
                href={getPageHref(Number(item))}
                aria-label={`Page ${item}`}
                className="page-link flex items-center justify-center small font-normal text-grey300 px-[14px] py-[8px] rounded-[4px] hover:bg-primary-50 hover:text-primary-400 w-[36px] h-[36px]"
                onClick={(e) => {
                  updateCurrentPage(e, Number(item));
                }}
              >
                {item}
              </Link>
            )}
          </li>
        ))}
        {currentPageNo !== totalPages && <li className="page-item">
          <Link
            data-testid="next"
            href={getPageHref(+currentPageNo + 1 <= totalPages ? +currentPageNo + 1 : totalPages)}
            aria-label="Next page"
            className="page-link flex items-center justify-center px-[14px] py-[8px] rounded-[4px] w-[36px] h-[36px] hover:bg-primary-50"
            onClick={(e) => {
              currentPageNo < totalPages
                ? updateCurrentPage(e, +currentPageNo + 1)
                : updateCurrentPage(e, +currentPageNo);
            }}
          >
            <svg
              width="7"
              height="12"
              viewBox="0 0 7 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1 1L6 6L1 11"
                stroke="#333F48"
                strokeWidth="1.67"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Link>
        </li>}
      </ul>
    </nav>
  );
};
